import { Inject, Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { of } from "rxjs";
import {
    catchError,
    filter,
    map,
    switchMap,
    tap,
    withLatestFrom,
} from "rxjs/operators";

import { Coordinates } from "@hermes/api-model-common";
import { Shop } from "@hermes/api-model-shop";
import { Context, LOCALE, WINDOW } from "@hermes/app-core";
import { Locale } from "@hermes/locale";
import { getDirectionUrl, isBaiduEngine } from "@hermes/map-utils";
import { getCoordinatesBasedOnLocale, ShopsService } from "@hermes/shop-utils";
import { ShellFacade } from "@hermes/states/shell";
import {
    SHELL_APPEARANCE_FULL,
    SHELL_APPEARANCE_NONE,
} from "@hermes/utils-generic/constants";
import { calculateDistance } from "@hermes/utils-generic/helpers";

import {
    fetchShops,
    fetchShopsFailure,
    fetchShopsSuccess,
    setExtraInfos,
    setMode,
    setSelectedLocation,
    setVisibleShopIds,
} from "../actions/shop-locator.actions";
import {
    DISTANCE_LIMIT_BEFORE_WARNING,
    MAX_SHOP_TO_DISPLAY,
} from "../constants/shop-locator.constant";
import {
    calculateVisibleShopsByCountry,
    calculateVisibleShopsByCountryAndCity,
    extendBounds,
    getShopsIdInBounds,
    isCity,
    isCoordinatesAndNoCountry,
    isCountry,
    isDefinedSelectedLocation,
    sortShopIdsByMeters,
} from "../helpers/shop-locator.helper";
import {
    SelectedLocationCity,
    SelectedLocationCoordinates,
    SelectedLocationCountry,
    ShopLocatorExtraInfos,
    ShopLocatorState,
    ShopsStateData,
} from "../reducers/shop-locator.state";
import {
    selectMode,
    selectShops,
    selectShopsParameters,
    selectShopsWithIgnored,
} from "../selectors/shop-locator.selectors";

@Injectable()
export class ShopLocatorEffects {
    public fetchShops$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fetchShops),
            withLatestFrom(
                this.store.select(selectShopsParameters),
                this.store.select(selectShops),
            ),
            switchMap(([{ parameters }, currentParameters, currentShops]) => {
                // If there are already shops and the query parameters are the same as those saved,
                // the query is not repeated.
                if (
                    Object.keys(currentShops).length > 0 &&
                    JSON.stringify(currentParameters) ===
                        JSON.stringify(parameters)
                ) {
                    return of(
                        fetchShopsSuccess({
                            shops: currentShops,
                            parameters,
                        }),
                    );
                }
                return this.shopsService.findShops(parameters).pipe(
                    map((shops: Shop[]) => {
                        let customShops = {};
                        shops.forEach(
                            (shop) =>
                                (customShops = {
                                    ...customShops,
                                    [shop.shopId]: shop,
                                }),
                        );
                        return customShops;
                    }),
                    map((shops: ShopsStateData) =>
                        fetchShopsSuccess({
                            shops,
                            parameters,
                        }),
                    ),
                    catchError((error) => {
                        if (error.status === 404) {
                            return of(
                                fetchShopsSuccess({
                                    shops: {},
                                    parameters,
                                }),
                            );
                        }

                        return of(
                            fetchShopsFailure({
                                errorMessage: error.message,
                            }),
                        );
                    }),
                );
            }),
        ),
    );

    public changeMode$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(setMode),
                tap(({ mode }) => {
                    this.shellFacade.setShell(
                        mode === "map-mobile"
                            ? SHELL_APPEARANCE_NONE
                            : SHELL_APPEARANCE_FULL,
                    );
                }),
            ),
        { dispatch: false },
    );

    /**
     * Each time the list is reloaded, force the scroll up.
     */
    public forceScrollToTheTop$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(setVisibleShopIds),
                filter(() => this.context.isInBrowserMode()),
                map(() => {
                    this.window.scrollTo(0, 0);
                }),
            ),
        { dispatch: false },
    );

    /**
     * For each new location selected, we have to recalculate the extra infos about shops.
     * If the map is not displayed, we have to calculate ourselves the shops to display
     */
    public selectedLocation$ = createEffect(() =>
        this.actions$.pipe(
            ofType(setSelectedLocation),
            filter(isDefinedSelectedLocation),
            withLatestFrom(
                this.store.select(selectShopsWithIgnored),
                this.store.select(selectMode),
            ),
            switchMap(([selectedLocation, shops, mode]) => {
                let extraInfos: ShopLocatorExtraInfos = {};

                // Calculation of extraInfos when the selected location is not a country and come from a user search.
                const coordinates: (shop: Shop) => Coordinates = (shop) =>
                    getCoordinatesBasedOnLocale(shop, this.locale);
                if (isCoordinatesAndNoCountry(selectedLocation)) {
                    Object.values(shops).forEach((shop) => {
                        const meters = calculateDistance(
                            selectedLocation.coordinates,
                            coordinates(shop),
                        );

                        const directionUrl = getDirectionUrl(this.locale)(
                            coordinates(shop),
                            selectedLocation.coordinates,
                        );
                        extraInfos = {
                            ...extraInfos,
                            [shop.shopId]: {
                                meters,
                                directionUrl,
                            },
                        };
                    });
                }

                const actionsToDispatch = [];
                actionsToDispatch.push(setExtraInfos({ extraInfos }));
                // If the map is not displayed, we have to calculate ourselves the stores to display
                // because it is not the map that gives us this information.
                if (mode === "no-map") {
                    const visibleShopIds = this.calculateVisibleShopIds(
                        shops,
                        extraInfos,
                        selectedLocation,
                    );
                    actionsToDispatch.push(
                        setVisibleShopIds({ visibleShopIds, source: "effect" }),
                    );
                }

                return actionsToDispatch;
            }),
        ),
    );

    constructor(
        private shopsService: ShopsService,
        private actions$: Actions,
        private store: Store<ShopLocatorState>,
        private context: Context,
        private shellFacade: ShellFacade,
        @Inject(LOCALE) private readonly locale: Locale,
        @Inject(WINDOW) private window: Window,
    ) {}

    public calculateVisibleShopIds(
        shops: ShopsStateData,
        extraInfos: ShopLocatorExtraInfos,
        selectedLocation:
            | SelectedLocationCoordinates
            | SelectedLocationCity
            | SelectedLocationCountry,
    ): string[] {
        if (isCity(selectedLocation)) {
            return Object.keys(
                calculateVisibleShopsByCountryAndCity(
                    shops,
                    selectedLocation.countryCode,
                    selectedLocation.city,
                ),
            );
        }

        if (isCountry(selectedLocation)) {
            return Object.keys(
                calculateVisibleShopsByCountry(
                    shops,
                    selectedLocation.countryCode,
                ),
            );
        }

        let shopIds = Object.keys(shops);

        // Sort shops from the closest to the farthest from the selected location
        // Note : if it's a country that is searched, we don't have extra info for the shops
        if (isCoordinatesAndNoCountry(selectedLocation)) {
            shopIds = sortShopIdsByMeters(shopIds, extraInfos);
        }

        if (isBaiduEngine(this.locale.countryCode)) {
            return shopIds.slice(0, MAX_SHOP_TO_DISPLAY);
        }

        // If Google, we uses the notion of bounds to know if the selected location contains shops or not.
        const bounds = new google.maps.LatLngBounds();

        // In some case, bounds are undefined, we only have coordiantes data.
        if (selectedLocation.bounds) {
            bounds.union({
                west: selectedLocation.bounds.west,
                east: selectedLocation.bounds.east,
                south: selectedLocation.bounds.south,
                north: selectedLocation.bounds.north,
            });
        } else {
            extendBounds(selectedLocation.coordinates, bounds);
        }
        const shopsIdInBounds = getShopsIdInBounds(bounds, shopIds, shops);

        // If there are shops in bounds, return only these shops.
        if (shopsIdInBounds.length > 0) {
            return shopsIdInBounds;
        }

        // If there are shops within 50 km of the selected location, they are displayed :
        if (isCoordinatesAndNoCountry(selectedLocation)) {
            const shopIdCloserThan = shopIds
                .slice(0, MAX_SHOP_TO_DISPLAY)
                .filter(
                    (shopId) =>
                        extraInfos[shopId].meters <=
                        DISTANCE_LIMIT_BEFORE_WARNING,
                );

            if (shopIdCloserThan.length > 0) {
                return shopIdCloserThan;
            }
        }

        // Else, simply return the 5 shops items that are closest.
        return shopIds.slice(0, MAX_SHOP_TO_DISPLAY);
    }
}
